/**
 * Project Editor Component
 *
 * Unified tabbed interface for creating and editing projects:
 * - General: Name, slug, subdomain, description, city, collaboration
 * - Features: Embedded FeatureFlagsEditor for module/flag configuration
 * - Spatial Units: admin_hierarchy level labels (GUS scale picker / scope bar)
 * - Storage: Cloudflare R2 provisioning and page-image settings
 * - Social Trends: TrueTrends integration
 * - Tenant Config: read-only view of the tenant's own configurations table
 *
 * Tabs that only ever wrote columns nothing reads -- Resources, AI Models,
 * Localization, Advanced and an orphaned AKS panel -- were removed. The columns
 * remain; see the project-editor audit for the per-field evidence.
 *
 * Modes:
 * - Create: projectId is undefined, shows organization selector
 * - Edit: projectId is provided, loads existing project data
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  IconButton,
  Stack,
  alpha,
  useTheme,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Extension as ExtensionIcon,
  Add as AddIcon,
  PeopleOutline as CollaborationIcon,
  CloudUpload as CloudUploadIcon,
  Map as MapIcon,
  TrendingUp as TrendsIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import api from '../services/api';
import FeatureFlagsEditor from './FeatureFlagsEditor';
import SocialTrendsConfigEditor from './SocialTrendsConfigEditor';
import TenantConfigPanel from './TenantConfigPanel';
import { introducedErrors } from './satisfaction/satisfactionSettings';
import { introducedExecutiveErrors } from './executive/executiveSettings';
import { SocialTrendsConfig, DEFAULT_SOCIAL_CONFIG } from '../types/socialConfig';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-editor-tabpanel-${index}`}
      aria-labelledby={`project-editor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface Tenant {
  id: string;
  tenant_id: string;
  organization_name: string;
}

interface ProjectEditorProps {
  projectId?: string;  // Optional - undefined for create mode
  onClose: () => void;
  onSaved?: () => void;
}

// Tab configuration with icons
const TABS = [
  { label: 'General', icon: <SettingsIcon fontSize="small" /> },
  { label: 'Features', icon: <ExtensionIcon fontSize="small" /> },
  { label: 'Spatial Units', icon: <MapIcon fontSize="small" /> },
  { label: 'Storage', icon: <CloudUploadIcon fontSize="small" /> },
  { label: 'Social Trends', icon: <TrendsIcon fontSize="small" /> },
  { label: 'Tenant Config', icon: <StorageIcon fontSize="small" /> },
];

// Panel indices are derived from TABS by label, never hand-numbered. Two panels
// used to declare index={6} -- the orphaned AKS "Infrastructure" block and
// Advanced -- so both rendered stacked on the Advanced tab. Deriving the index
// makes that class of defect unrepresentable, and lets a tab be removed without
// renumbering every panel below it.
const tabIndex = (label: string) => TABS.findIndex(t => t.label === label);
const TENANT_CONFIG_TAB = tabIndex('Tenant Config');

// Default feature flags for new projects or projects with empty flags
// IMPORTANT: Module names MUST match the GUS v2 canonical contract (PR #401):
// Sidebar ID  →  Config path              →  Admin portal enabled_modules key
// gus         →  modules.gus_chat         →  gus_chat
// atlas       →  modules.atlas            →  atlas (NOT social_atlas)
// reports     →  modules.reports          →  reports
// map         →  modules.map             →  map
// fieldnotes  →  modules.fieldnotes      →  fieldnotes (NOT dashboard)
// tools       →  modules.tools           →  tools
// assessments →  modules.assessments     →  assessments
// datamanagement → modules.data_management → data_management
// Default when key absent: true (backward-compatible — admin opts OUT with explicit false)
const DEFAULT_FEATURE_FLAGS = {
  modules: {
    atlas: {
      enabled: true,
      tabs: {
        overview: { enabled: true },
        demographics: { enabled: true },
        economic: { enabled: true },
        infrastructure: { enabled: true },
        environment: { enabled: true },
      },
    },
    gus_chat: { enabled: true },
    data_management: { enabled: true },
    assessments: { enabled: true },
    fieldnotes: { enabled: true },
    maps_library: { enabled: false },
    reports: { enabled: true },
    map: { enabled: true },
    tools: { enabled: true },
    settings: { enabled: true },
  },
};

// Default localization config
const DEFAULT_LOCALIZATION_CONFIG = {
  default_language: 'en',
  supported_languages: ['en'],
  rtl_languages: ['ar'],
  date_format: 'YYYY-MM-DD',
  timezone: 'UTC',
};

// Starter levels for the admin-hierarchy editor. `code` is the stable
// aggregate_by value GUS endpoints understand — only the labels are
// tenant-specific (e.g. YUWDDA renames community→"Hara", district→"Governorate").
// Saved as NULL when the custom toggle is off, which tells GUS to use its
// built-in default hierarchy.
const ADMIN_HIERARCHY_STARTER_LEVELS = [
  { code: 'hex', label: 'Hex', plural: 'Hexes', label_ar: 'سداسية', plural_ar: 'السداسيات', default: false },
  { code: 'community', label: 'Community', plural: 'Communities', label_ar: 'مجمع', plural_ar: 'المجمعات', default: false },
  { code: 'district', label: 'District', plural: 'Districts', label_ar: 'حي', plural_ar: 'الأحياء', default: true },
];

// Helper to check if an object is empty or has no meaningful data
const isEmptyConfig = (obj: any): boolean => {
  if (!obj) return true;
  if (typeof obj !== 'object') return false;
  const keys = Object.keys(obj);
  if (keys.length === 0) return true;
  // Check if modules object is empty
  if (obj.modules && Object.keys(obj.modules).length === 0) return true;
  return false;
};

const ProjectEditor: React.FC<ProjectEditorProps> = ({ projectId, onClose, onSaved }) => {
  const theme = useTheme();
  const isCreateMode = !projectId;
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [provisioningR2, setProvisioningR2] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    // General
    project_name: '',
    project_slug: '',
    subdomain: '',
    subdomain_status: 'pending' as 'pending' | 'approved' | 'active' | 'suspended',
    deployment_url: '',
    description: '',
    tenant_id: '',
    organization_name: '',

    // City Configuration
    city_name: '',
    city_context: '',

    // Collaboration Settings
    allow_external_collaborators: false,
    external_collaborator_roles: ['viewer'] as string[],

    // R2 Storage Configuration
    enable_page_images: false,
    r2_endpoint_url: '',
    r2_access_key_id: '',
    r2_secret_access_key: '',
    r2_bucket: 'gus-documents',
    r2_cdn_domain: '',
    page_image_max_pages: 0,
  });

  // Feature flags state - initialize with defaults for new projects
  const [featureFlags, setFeatureFlags] = useState<any>(DEFAULT_FEATURE_FLAGS);
  // What the database holds now, so a save is judged on what it changes (see handleSave).
  const [storedFeatureFlags, setStoredFeatureFlags] = useState<any>(null);
  const [backendFeatureFlags, setBackendFeatureFlags] = useState<any>({});
  const [frontendFeatureFlags, setFrontendFeatureFlags] = useState<any>({});
  const [settingsConfig, setSettingsConfig] = useState<any>({});
  const [dataSourcesConfig, setDataSourcesConfig] = useState<any>({});
  const [mapStylesConfig, setMapStylesConfig] = useState<any>({});
  const [localizationConfig, setLocalizationConfig] = useState<any>(DEFAULT_LOCALIZATION_CONFIG);
  const [themeConfig, setThemeConfig] = useState<any>({});
  // null = project uses GUS's built-in default hierarchy (no override stored)
  const [adminHierarchy, setAdminHierarchy] = useState<any>(null);
  // Social Trends configuration
  const [socialTrendsConfig, setSocialTrendsConfig] = useState<SocialTrendsConfig>(DEFAULT_SOCIAL_CONFIG);
  // Backend configuration (GUS Chat and Security settings)
  const [backendConfig, setBackendConfig] = useState<any>({
    document_only_mode: false,
    enable_llm_classification: true,
    enable_agent_dataset_tools: false,
    enable_prompt_guard: true,
    enable_quality_metrics: true,
    require_email_verification: false,
    session_timeout_hours: 24,
  });

  // Load tenants for create mode
  const loadTenants = async (signal?: AbortSignal) => {
    try {
      const response = await api.get('/tenants', { signal });
      setTenants(response.data.tenants || response.data || []);
    } catch (err: any) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Failed to load tenants:', err);
      }
    }
  };

  // Load project data (edit mode only)
  const loadProject = async (signal?: AbortSignal) => {
    if (!projectId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/projects/${projectId}`, { signal });
      const project = response.data;

      // Populate form
      setFormData({
        project_name: project.project_name || '',
        project_slug: project.project_slug || '',
        subdomain: project.subdomain || project.project_slug || '',
        subdomain_status: project.subdomain_status || 'pending',
        deployment_url: project.deployment_url || '',
        description: project.description || '',
        tenant_id: project.tenant_id || '',
        organization_name: project.organization_name || '',
        city_name: project.city_name || '',
        city_context: project.city_context || '',
        // Collaboration Settings
        allow_external_collaborators: project.allow_external_collaborators || false,
        external_collaborator_roles: project.external_collaborator_roles || ['viewer'],
        // R2 Storage Configuration
        enable_page_images: project.enable_page_images || false,
        r2_endpoint_url: project.r2_endpoint_url || '',
        r2_access_key_id: project.r2_access_key_id || '',
        r2_secret_access_key: '',  // Never load secret, only update
        r2_bucket: project.r2_bucket || 'gus-documents',
        r2_cdn_domain: project.r2_cdn_domain || '',
        page_image_max_pages: project.page_image_max_pages || 0,
      });

      setStoredFeatureFlags(project.feature_flags ?? null);
      // Load feature flags - use defaults if empty
      if (project.feature_flags && !isEmptyConfig(project.feature_flags)) {
        setFeatureFlags(project.feature_flags);
      } else {
        // Initialize with defaults for projects with empty/missing feature flags
        setFeatureFlags(DEFAULT_FEATURE_FLAGS);
      }
      if (project.backend_feature_flags) {
        setBackendFeatureFlags(project.backend_feature_flags);
      }
      if (project.frontend_feature_flags) {
        setFrontendFeatureFlags(project.frontend_feature_flags);
      }
      if (project.settings_config) {
        setSettingsConfig(project.settings_config);
      }
      if (project.data_sources_config) {
        setDataSourcesConfig(project.data_sources_config);
      }
      if (project.map_styles_config) {
        setMapStylesConfig(project.map_styles_config);
      }
      // Load localization config - use defaults if empty
      // Note: Currency and timezone are now stored as explicit columns, not in localization_config JSON
      if (project.localization_config && Object.keys(project.localization_config).length > 0) {
        setLocalizationConfig(project.localization_config);
      } else {
        setLocalizationConfig(DEFAULT_LOCALIZATION_CONFIG);
      }
      if (project.theme_config) {
        setThemeConfig(project.theme_config);
      }
      setAdminHierarchy(project.admin_hierarchy ?? null);
      // Load backend configuration (GUS Chat and Security settings)
      setBackendConfig({
        document_only_mode: project.document_only_mode ?? false,
        enable_llm_classification: project.enable_llm_classification ?? true,
        enable_agent_dataset_tools: project.enable_agent_dataset_tools ?? false,
        enable_prompt_guard: project.enable_prompt_guard ?? true,
        enable_quality_metrics: project.enable_quality_metrics ?? true,
        require_email_verification: project.require_email_verification ?? false,
        session_timeout_hours: project.session_timeout_hours ?? 24,
      });
      // Load social trends configuration
      setSocialTrendsConfig({
        social_trends_enabled: project.social_trends_enabled ?? false,
        truetrends_api_url: project.truetrends_api_url ?? null,
        truetrends_api_key_configured: !!project.truetrends_api_key_ref,
        social_sync_frequency_hours: project.social_sync_frequency_hours ?? 4,
        social_data_retention_days: project.social_data_retention_days ?? 0,
        social_custom_topics: project.social_custom_topics ?? [],
        social_platforms_enabled: project.social_platforms_enabled ?? DEFAULT_SOCIAL_CONFIG.social_platforms_enabled,
        social_spike_threshold: project.social_spike_threshold ?? 2.5,
        social_spike_lookback_days: project.social_spike_lookback_days ?? 7,
      });
    } catch (err: any) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        setError(err.response?.data?.detail || 'Failed to load project');
      }
    } finally {
      // Skip state update if the request was aborted (component unmounted)
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    if (isCreateMode) {
      loadTenants(controller.signal);
    } else {
      loadProject(controller.signal);
    }

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Auto-generate slug from project name in create mode
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 63);
  };

  const handleProjectNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      project_name: name,
      // Auto-generate slug only in create mode and if slug hasn't been manually edited
      ...(isCreateMode && (prev.project_slug === '' || prev.project_slug === generateSlug(prev.project_name))
        ? { project_slug: generateSlug(name), subdomain: generateSlug(name) }
        : {}),
    }));
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFeatureFlagsChange = useCallback((updates: any) => {
    if (updates.featureFlags) setFeatureFlags(updates.featureFlags);
    if (updates.backendFeatureFlags) setBackendFeatureFlags(updates.backendFeatureFlags);
    if (updates.frontendFeatureFlags) setFrontendFeatureFlags(updates.frontendFeatureFlags);
    if (updates.settingsConfig) setSettingsConfig(updates.settingsConfig);
    if (updates.dataSourcesConfig) setDataSourcesConfig(updates.dataSourcesConfig);
    if (updates.mapStylesConfig) setMapStylesConfig(updates.mapStylesConfig);
    if (updates.localizationConfig) setLocalizationConfig(updates.localizationConfig);
    if (updates.themeConfig) setThemeConfig(updates.themeConfig);
    if (updates.backendConfig) setBackendConfig(updates.backendConfig);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.project_name.trim()) {
      setError('Project name is required');
      setSaving(false);
      return;
    }
    if (isCreateMode && !formData.tenant_id) {
      setError('Please select an organization');
      setSaving(false);
      return;
    }
    if (isCreateMode && !formData.project_slug.trim()) {
      setError('Project identifier is required');
      setSaving(false);
      return;
    }
    // GUS ignores a City Satisfaction value it cannot use, so this save must not add one.
    // The backend refuses it too; this says so before the round trip. Each value is
    // marked in red on the Features tab. A value already stored and left as it was is
    // shown there too, but does not block an unrelated save.
    const satisfactionProblems = introducedErrors(featureFlags, isCreateMode ? null : storedFeatureFlags);
    if (satisfactionProblems.length) {
      setError(
        'City Satisfaction settings not saved (Features tab) / لم تُحفظ إعدادات رضا المدينة (تبويب الميزات): '
        + satisfactionProblems
          .map((e) => `${e.path.replace('modules.atlas.tabs.satisfaction.', '')}: ${e.message.en} ${e.message.ar}`)
          .join('; ')
      );
      setSaving(false);
      return;
    }
    // The same for the Leadership Brief (GUS's Atlas "executive" tab), checked as the backend checks it.
    const briefProblems = introducedExecutiveErrors(featureFlags, isCreateMode ? null : storedFeatureFlags);
    if (briefProblems.length) {
      setError(
        'Leadership Brief settings not saved (Features tab) / لم تُحفظ إعدادات الموجز القيادي (تبويب الميزات): '
        + briefProblems
          .map((e) => `${e.path.replace('modules.atlas.tabs.executive.', '')}: ${e.message.en} ${e.message.ar}`)
          .join('; ')
      );
      setSaving(false);
      return;
    }

    try {
      const projectData = {
        ...formData,
        feature_flags: featureFlags,
        backend_feature_flags: backendFeatureFlags,
        frontend_feature_flags: frontendFeatureFlags,
        settings_config: settingsConfig,
        data_sources_config: dataSourcesConfig,
        map_styles_config: mapStylesConfig,
        localization_config: localizationConfig,
        theme_config: themeConfig,
        // null clears the override → GUS falls back to its default hierarchy
        admin_hierarchy: adminHierarchy,
        // Backend Configuration (GUS Chat and Security settings)
        document_only_mode: backendConfig.document_only_mode,
        enable_llm_classification: backendConfig.enable_llm_classification,
        enable_agent_dataset_tools: backendConfig.enable_agent_dataset_tools,
        enable_prompt_guard: backendConfig.enable_prompt_guard,
        enable_quality_metrics: backendConfig.enable_quality_metrics,
        require_email_verification: backendConfig.require_email_verification,
        session_timeout_hours: backendConfig.session_timeout_hours,
        // Social Trends Configuration
        social_trends_enabled: socialTrendsConfig.social_trends_enabled,
        truetrends_api_url: socialTrendsConfig.truetrends_api_url,
        social_sync_frequency_hours: socialTrendsConfig.social_sync_frequency_hours,
        social_data_retention_days: socialTrendsConfig.social_data_retention_days,
        social_custom_topics: socialTrendsConfig.social_custom_topics,
        social_platforms_enabled: socialTrendsConfig.social_platforms_enabled,
        social_spike_threshold: socialTrendsConfig.social_spike_threshold,
        social_spike_lookback_days: socialTrendsConfig.social_spike_lookback_days,
      };

      if (isCreateMode) {
        // Create new project
        await api.post('/projects', projectData);
        setSuccess('Project created successfully');
      } else {
        // Update existing project
        await api.put(`/projects/${projectId}`, projectData);
        setStoredFeatureFlags(featureFlags);
        setSuccess('Project updated successfully');
      }

      if (onSaved) onSaved();
    } catch (err: any) {
      const action = isCreateMode ? 'create' : 'update';
      setError(err.response?.data?.detail || `Failed to ${action} project`);
    } finally {
      setSaving(false);
    }
  };

  // Provision R2 Storage automatically via Cloudflare API
  const handleProvisionR2 = async () => {
    if (!projectId) {
      setError('Save the project first before provisioning R2 storage');
      return;
    }

    setProvisioningR2(true);
    setError(null);

    try {
      const response = await api.post(`/admin/projects/${projectId}/provision-r2`, {
        location_hint: 'ENAM'  // Eastern North America - closest to Azure
      });

      if (response.data.success) {
        // Update form with provisioned credentials
        setFormData(prev => ({
          ...prev,
          enable_page_images: true,
          r2_endpoint_url: response.data.endpoint_url,
          r2_access_key_id: response.data.access_key_id,
          r2_bucket: response.data.bucket_name,
        }));
        setSuccess(`R2 storage provisioned: ${response.data.bucket_name}`);
      } else {
        setError(response.data.message || 'Failed to provision R2 storage');
      }
    } catch (err: any) {
      console.error('R2 provisioning error:', err);
      setError(err.response?.data?.detail || 'Failed to provision R2 storage');
    } finally {
      setProvisioningR2(false);
    }
  };

  // Common styles for form fields
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
    },
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 12,
        flexDirection: 'column',
        gap: 2,
      }}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">Loading project...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        mb: 4,
      }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
            {isCreateMode ? 'Create Project' : 'Edit Project'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isCreateMode
              ? 'Set up a new GUS project with all configuration options'
              : formData.project_name || 'Configure all aspects of this project'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {!isCreateMode && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => loadProject()}
              disabled={saving}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
              }}
            >
              Reload
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (isCreateMode ? <AddIcon /> : <SaveIcon />)}
            onClick={handleSave}
            disabled={saving}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
            }}
          >
            {saving ? 'Saving...' : (isCreateMode ? 'Create Project' : 'Save Changes')}
          </Button>
          <IconButton
            onClick={onClose}
            disabled={saving}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Alerts */}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 56,
              px: 3,
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
          }}
        >
          {(isCreateMode ? TABS.filter(t => t.label !== 'Tenant Config') : TABS).map((tab, index) => (
            <Tab
              key={tab.label}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              sx={{ gap: 1 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        {/* General Tab */}
        <TabPanel value={activeTab} index={tabIndex('General')}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<InfoIcon />}
                sx={{ borderRadius: 2 }}
              >
                {isCreateMode
                  ? 'Set up your new project. The identifier will be used in URLs and cannot be changed later.'
                  : 'Basic project information. The identifier is used in URLs and cannot be changed after creation.'}
              </Alert>
            </Grid>

            {/* Organization selector - only in create mode */}
            {isCreateMode && (
              <Grid item xs={12}>
                <FormControl fullWidth required sx={fieldSx}>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={formData.tenant_id}
                    onChange={e => {
                      const selectedTenant = tenants.find(t => t.id === e.target.value);
                      handleInputChange('tenant_id', e.target.value);
                      if (selectedTenant) {
                        handleInputChange('organization_name', selectedTenant.organization_name);
                      }
                    }}
                    label="Organization"
                  >
                    {tenants.map(tenant => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.organization_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.project_name}
                onChange={e => handleProjectNameChange(e.target.value)}
                helperText="Display name for this project"
                required
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Identifier"
                value={formData.project_slug}
                onChange={e => handleInputChange('project_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                disabled={!isCreateMode}
                helperText={isCreateMode
                  ? 'URL-safe identifier (auto-generated from name, can be customized)'
                  : 'URL-safe identifier (cannot be changed)'}
                inputProps={{ maxLength: 63 }}
                required
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subdomain"
                value={formData.subdomain}
                onChange={e => handleInputChange('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                helperText={
                  formData.deployment_url
                    ? `GUS deployment URL: ${formData.deployment_url}`
                    : formData.subdomain
                    ? `GUS deployment URL: https://${formData.subdomain}.gus.silacities.com`
                    : 'Subdomain for your GUS deployment'
                }
                inputProps={{ maxLength: 63 }}
                InputProps={{
                  endAdornment: !isCreateMode && formData.subdomain_status && (
                    <Chip
                      label={formData.subdomain_status}
                      size="small"
                      color={
                        formData.subdomain_status === 'active' ? 'success' :
                        formData.subdomain_status === 'approved' ? 'info' :
                        formData.subdomain_status === 'pending' ? 'warning' : 'error'
                      }
                      sx={{ mr: 1 }}
                    />
                  )
                }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                helperText="Optional description of this project"
                sx={fieldSx}
              />
            </Grid>
            {/* Organization display - only in edit mode */}
            {!isCreateMode && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Organization"
                  value={formData.organization_name}
                  disabled
                  helperText="Owning organization (cannot be changed)"
                  sx={fieldSx}
                />
              </Grid>
            )}
            <Grid item xs={12} md={isCreateMode ? 12 : 6}>
              <TextField
                fullWidth
                label="City Name"
                value={formData.city_name}
                onChange={e => handleInputChange('city_name', e.target.value)}
                helperText="City or region name"
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.city_context}
                onChange={e => handleInputChange('city_context', e.target.value)}
                helperText="e.g., UAE, Saudi Arabia, Qatar (used for nationality labels)"
                placeholder="UAE"
                sx={fieldSx}
              />
            </Grid>

            {/* Collaboration Settings - Only show in edit mode */}
            {!isCreateMode && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CollaborationIcon color="primary" />
                    <Typography variant="h6">External Collaboration</Typography>
                  </Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Allow users from other organizations to access this project. External collaborators
                    are limited to specific roles and cannot be assigned admin privileges.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.allow_external_collaborators}
                        onChange={(e) => handleInputChange('allow_external_collaborators', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">Allow External Collaborators</Typography>
                        <Typography variant="body2" color="text.secondary">
                          When enabled, users from other organizations can be added to this project
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                {formData.allow_external_collaborators && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Allowed Roles for External Users
                    </Typography>
                    <FormGroup row>
                      {['viewer', 'analyst', 'editor'].map((role) => (
                        <FormControlLabel
                          key={role}
                          control={
                            <Checkbox
                              checked={formData.external_collaborator_roles.includes(role)}
                              onChange={(e) => {
                                const newRoles = e.target.checked
                                  ? [...formData.external_collaborator_roles, role]
                                  : formData.external_collaborator_roles.filter(r => r !== role);
                                handleInputChange('external_collaborator_roles', newRoles);
                              }}
                            />
                          }
                          label={role.charAt(0).toUpperCase() + role.slice(1)}
                        />
                      ))}
                    </FormGroup>
                    <Typography variant="caption" color="text.secondary">
                      Note: Admin role is never available for external collaborators
                    </Typography>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </TabPanel>

        {/* Features Tab */}
        <TabPanel value={activeTab} index={tabIndex('Features')}>
          <FeatureFlagsEditor
            projectId={projectId}
            featureFlags={featureFlags}
            backendFeatureFlags={backendFeatureFlags}
            frontendFeatureFlags={frontendFeatureFlags}
            settingsConfig={settingsConfig}
            dataSourcesConfig={dataSourcesConfig}
            mapStylesConfig={mapStylesConfig}
            localizationConfig={localizationConfig}
            themeConfig={themeConfig}
            backendConfig={backendConfig}
            onChange={handleFeatureFlagsChange}
            loading={saving}
            readOnly={saving}
          />
        </TabPanel>

        {/* Spatial Units Tab */}
        <TabPanel value={activeTab} index={tabIndex('Spatial Units')}>
          <Grid container spacing={3}>
            {/* Spatial Units (Admin Hierarchy) */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Spatial Units (Admin Hierarchy)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                What this project calls its administrative levels. GUS uses these
                labels in the scale picker, scope bar and chat answers. The level
                code is fixed (it's what the data tables are keyed on) — only the
                display names change. Example: YUWDDA renames Community to
                "Hara" and District to "Governorate".
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={adminHierarchy !== null}
                    onChange={e =>
                      setAdminHierarchy(
                        e.target.checked
                          ? { levels: ADMIN_HIERARCHY_STARTER_LEVELS.map(l => ({ ...l })) }
                          : null
                      )
                    }
                  />
                }
                label="Use custom spatial-unit labels (off = GUS default: Hex / Community / District)"
              />
            </Grid>
            {adminHierarchy?.levels?.map((level: any, idx: number) => (
              <Grid item xs={12} key={level.code}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  <Chip label={level.code} sx={{ fontFamily: 'monospace', minWidth: 90 }} />
                  <TextField
                    label="Label"
                    size="small"
                    value={level.label || ''}
                    onChange={e => {
                      const levels = adminHierarchy.levels.map((l: any, i: number) =>
                        i === idx ? { ...l, label: e.target.value } : l);
                      setAdminHierarchy({ levels });
                    }}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    label="Plural"
                    size="small"
                    value={level.plural || ''}
                    onChange={e => {
                      const levels = adminHierarchy.levels.map((l: any, i: number) =>
                        i === idx ? { ...l, plural: e.target.value } : l);
                      setAdminHierarchy({ levels });
                    }}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    label="Arabic Label"
                    size="small"
                    value={level.label_ar || ''}
                    onChange={e => {
                      const levels = adminHierarchy.levels.map((l: any, i: number) =>
                        i === idx ? { ...l, label_ar: e.target.value } : l);
                      setAdminHierarchy({ levels });
                    }}
                    inputProps={{ dir: 'rtl' }}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    label="Arabic Plural"
                    size="small"
                    value={level.plural_ar || ''}
                    onChange={e => {
                      const levels = adminHierarchy.levels.map((l: any, i: number) =>
                        i === idx ? { ...l, plural_ar: e.target.value } : l);
                      setAdminHierarchy({ levels });
                    }}
                    inputProps={{ dir: 'rtl' }}
                    sx={{ width: 150 }}
                  />
                  <Chip
                    label="Default level"
                    onClick={() => {
                      const levels = adminHierarchy.levels.map((l: any, i: number) =>
                        ({ ...l, default: i === idx }));
                      setAdminHierarchy({ levels });
                    }}
                    color={level.default ? 'primary' : 'default'}
                    variant={level.default ? 'filled' : 'outlined'}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Storage Tab */}
        <TabPanel value={activeTab} index={tabIndex('Storage')}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<InfoIcon />}
                sx={{ borderRadius: 2 }}
              >
                Configure Cloudflare R2 storage for PDF page image citations.
                When enabled, document uploads will generate page thumbnails for visual citations.
              </Alert>
            </Grid>

            {/* Provision R2 Button - only show in edit mode when not already configured */}
            {!isCreateMode && !formData.r2_endpoint_url && (
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Auto-Provision R2 Storage
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Automatically create an R2 bucket and credentials for this project
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={handleProvisionR2}
                      disabled={provisioningR2}
                      startIcon={provisioningR2 ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                      sx={{ borderRadius: 2, minWidth: 180 }}
                    >
                      {provisioningR2 ? 'Provisioning...' : 'Provision R2 Storage'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Show configured status when R2 is already set up */}
            {formData.r2_endpoint_url && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  R2 storage is configured. Bucket: <strong>{formData.r2_bucket}</strong>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enable_page_images}
                    onChange={e => handleInputChange('enable_page_images', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>Enable PDF Page Images</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Store PDF page images in R2 for visual citation thumbnails
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {formData.enable_page_images && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="R2 Endpoint URL"
                    value={formData.r2_endpoint_url}
                    onChange={e => handleInputChange('r2_endpoint_url', e.target.value)}
                    placeholder="https://xxxxxxxx.r2.cloudflarestorage.com"
                    helperText="Cloudflare R2 S3-compatible endpoint URL"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Access Key ID"
                    value={formData.r2_access_key_id}
                    onChange={e => handleInputChange('r2_access_key_id', e.target.value)}
                    helperText="R2 API access key ID"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Secret Access Key"
                    type="password"
                    value={formData.r2_secret_access_key}
                    onChange={e => handleInputChange('r2_secret_access_key', e.target.value)}
                    helperText="Leave blank to keep existing secret"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bucket Name"
                    value={formData.r2_bucket}
                    onChange={e => handleInputChange('r2_bucket', e.target.value)}
                    placeholder="gus-documents"
                    helperText="R2 bucket name for storing page images"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="CDN Domain (Optional)"
                    value={formData.r2_cdn_domain}
                    onChange={e => handleInputChange('r2_cdn_domain', e.target.value)}
                    placeholder="cdn.example.com"
                    helperText="Custom CDN domain for faster image loading"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Pages for Image Generation"
                    value={formData.page_image_max_pages || 0}
                    onChange={e => handleInputChange('page_image_max_pages', parseInt(e.target.value) || 0)}
                    placeholder="0"
                    helperText="0 = unlimited. Set limit to reduce processing time for very large PDFs."
                    InputProps={{ inputProps: { min: 0 } }}
                    sx={fieldSx}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        {/* Social Trends Tab */}
        <TabPanel value={activeTab} index={tabIndex('Social Trends')}>
          <SocialTrendsConfigEditor
            projectId={projectId}
            config={socialTrendsConfig}
            onChange={setSocialTrendsConfig}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={TENANT_CONFIG_TAB}>
          {projectId && <TenantConfigPanel projectId={projectId} />}
        </TabPanel>
      </Paper>

      {/* Footer Actions */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
        mt: 3,
        pt: 3,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : (isCreateMode ? <AddIcon /> : <SaveIcon />)}
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
          }}
        >
          {saving ? 'Saving...' : (isCreateMode ? 'Create Project' : 'Save All Changes')}
        </Button>
      </Box>
    </Box>
  );
};

export default ProjectEditor;
